(function (root, factory) {
  const api = factory();
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  } else {
    root.Codec = api;
  }
}(typeof globalThis !== "undefined" ? globalThis : this, () => {
  function alawByteToPcm16(value) {
    const a = value ^ 0x55;
    let sample = (a & 0x0f) << 4;
    const segment = (a & 0x70) >> 4;
    sample += 8;
    if (segment >= 1) sample += 0x100;
    if (segment > 1) sample <<= segment - 1;
    return a & 0x80 ? sample : -sample;
  }

  function mulawByteToPcm16(value) {
    const u = ~value & 0xff;
    let sample = ((u & 0x0f) << 3) + 0x84;
    sample <<= (u & 0x70) >> 4;
    return u & 0x80 ? 0x84 - sample : sample - 0x84;
  }

  const G711_DECODERS = { pcma: alawByteToPcm16, pcmu: mulawByteToPcm16 };
  const G711_SAMPLE_RATE = 8000;
  const RTP_TIMESTAMP_MODULUS = 0x1_0000_0000;
  const RTP_TIMESTAMP_HALF_RANGE = 0x8000_0000;
  const MAX_AUDIO_TIMESTAMP_GAP_SAMPLES = G711_SAMPLE_RATE * 2;
  const DEFAULT_AUDIO_DELAY_SECONDS = 0.05;
  const MAX_AUDIO_DELAY_SECONDS = 2;
  const MAX_AUDIO_BUFFER_SECONDS = MAX_AUDIO_DELAY_SECONDS + 1;
  const AUDIO_WORKLET_PROCESSOR = "g711-pcm-player";
  const MIN_AUDIO_PLAYBACK_RATE = 0.5;
  const MAX_AUDIO_PLAYBACK_RATE = 2;

  function defaultAudioWorkletUrl() {
    // Absolute site-root URL — relative path breaks under /surveillance/...
    const publicUrl = typeof process !== "undefined" && process.env.PUBLIC_URL;
    if (publicUrl) return `${publicUrl.replace(/\/$/, "")}/g711-audio-worklet.js`;
    const baseUrl = typeof document !== "undefined"
      ? document.baseURI
      : (typeof globalThis !== "undefined" && globalThis.location?.href);
    return baseUrl ? new URL("/g711-audio-worklet.js", baseUrl).toString() : "/g711-audio-worklet.js";
  }

  function normalizeAudioTimestamp(value) {
    if (value === undefined || value === null || value === "") return null;
    const timestamp = typeof value === "number" ? value : Number(value);
    return Number.isInteger(timestamp) && timestamp >= 0 && timestamp < RTP_TIMESTAMP_MODULUS
      ? timestamp
      : null;
  }

  function resampleG711(payload, decode, sampleRate) {
    const ratio = sampleRate / G711_SAMPLE_RATE;
    const length = Math.max(1, Math.floor(payload.length * ratio));
    const output = new Float32Array(length);
    for (let index = 0; index < length; index += 1) {
      const position = index / ratio;
      const firstIndex = Math.min(payload.length - 1, Math.floor(position));
      const secondIndex = Math.min(payload.length - 1, firstIndex + 1);
      const fraction = position - firstIndex;
      const first = decode(payload[firstIndex]);
      const second = decode(payload[secondIndex]);
      output[index] = (first + ((second - first) * fraction)) / 32768;
    }
    return output;
  }

  class StreamingLinearResampler {
    constructor(sampleRate) {
      this.sampleRate = sampleRate;
      this.previousSample = null;
      this.phase = 0;
    }

    reset() {
      this.previousSample = null;
      this.phase = 0;
    }

    process(length, sampleAt) {
      if (!length) return new Float32Array(0);
      let inputIndex = 0;
      if (this.previousSample === null) {
        this.previousSample = sampleAt(0);
        inputIndex = 1;
      }
      const output = [];
      for (; inputIndex < length; inputIndex += 1) {
        const currentSample = sampleAt(inputIndex);
        while (this.phase < this.sampleRate) {
          const fraction = this.phase / this.sampleRate;
          output.push(this.previousSample + ((currentSample - this.previousSample) * fraction));
          this.phase += G711_SAMPLE_RATE;
        }
        this.phase -= this.sampleRate;
        this.previousSample = currentSample;
      }
      return Float32Array.from(output);
    }

    processG711(payload, decode) {
      return this.process(payload.length, (index) => decode(payload[index]) / 32768);
    }

    processSilence(length) {
      return this.process(length, () => 0);
    }
  }

  function createAudioWorkletRenderer(context, workletUrl, bufferSeconds = MAX_AUDIO_BUFFER_SECONDS) {
    const AudioWorkletNodeConstructor = typeof globalThis !== "undefined"
      ? globalThis.AudioWorkletNode
      : undefined;
    if (!context?.audioWorklet || typeof context.audioWorklet.addModule !== "function"
      || typeof AudioWorkletNodeConstructor !== "function") {
      return Promise.reject(new Error("AudioWorklet is unavailable"));
    }
    return context.audioWorklet.addModule(workletUrl).then(() => {
      const node = new AudioWorkletNodeConstructor(context, AUDIO_WORKLET_PROCESSOR, {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: { bufferSeconds },
      });
      node.connect(context.destination);
      return {
        set onmessage(handler) {
          node.port.onmessage = handler;
        },
        send(message) {
          const transfer = message.samples?.buffer ? [message.samples.buffer] : [];
          node.port.postMessage(message, transfer);
        },
        close() {
          node.port.postMessage({ type: "close" });
          node.disconnect?.();
        },
      };
    });
  }

  class G711AudioSink {
    constructor(contextFactory, options = {}) {
      this.contextFactory = contextFactory;
      this.workletUrl = options.workletUrl || defaultAudioWorkletUrl();
      this.rendererFactory = options.rendererFactory || createAudioWorkletRenderer;
      this.bufferSeconds = options.bufferSeconds;
      this.onRendererMessage = options.onRendererMessage || (() => {});
      this.context = null;
      this.renderer = null;
      this.rendererPromise = null;
      this.rendererGeneration = 0;
      this.rendererError = null;
      this.pendingMessages = [];
      this.playbackStarted = false;
      this.playbackRate = 1;
      this.nextAudioTimestamp = null;
      this.audioCodec = null;
      this.resampler = null;
      this.submittedFrames = 0;
      this.outputPosition = null;
      this.outputHistory = [];
      this.playbackHeld = false;
    }

    async unlock() {
      if (!this.context) this.context = this.contextFactory();
      if (this.rendererError && !this.renderer && !this.rendererPromise) this.rendererError = null;
      const context = this.context;
      const generation = this.rendererGeneration;
      if (context.state === "suspended" || typeof context.resume === "function") {
        await context.resume?.();
      }
      if (this.context !== context || this.rendererGeneration !== generation) return null;
      if (context.state !== undefined && context.state !== "running") {
        throw new Error("Audio output is not running");
      }
      this.ensureRenderer();
      const rendererPromise = this.rendererPromise;
      await rendererPromise;
      if (this.context !== context || this.rendererGeneration !== generation) return null;
      if (this.rendererError) throw this.rendererError;
      return this.renderer;
    }

    ensureRenderer() {
      if (!this.context || this.renderer || this.rendererPromise || this.rendererError) return;
      const context = this.context;
      const generation = this.rendererGeneration;
      let renderer;
      try {
        renderer = this.rendererFactory(context, this.workletUrl, this.bufferSeconds);
      } catch (error) {
        this.failRenderer(error, context, generation);
        return;
      }
      this.rendererPromise = Promise.resolve(renderer)
        .then((readyRenderer) => {
          if (!readyRenderer) throw new Error("Audio renderer was not created");
          if (this.context !== context || this.rendererGeneration !== generation) {
            readyRenderer.close?.();
            return;
          }
          this.renderer = readyRenderer;
          this.renderer.onmessage = (event) => {
            const message = event?.data || event || {};
            if (message.type === "playback-position") {
              this.outputPosition = message;
              this.outputHistory.push(message);
              if (this.outputHistory.length > 250) this.outputHistory.shift();
            }
            if (message.type === "overflow") this.resetPlayback();
            this.onRendererMessage(message);
          };
          const pending = this.pendingMessages.splice(0);
          for (const message of pending) this.sendMessage(message);
        })
        .catch((error) => {
          this.failRenderer(error, context, generation);
        })
        .finally(() => {
          if (this.context === context && this.rendererGeneration === generation) this.rendererPromise = null;
        });
    }

    failRenderer(error, context, generation) {
      if (this.context !== context || this.rendererGeneration !== generation) return;
      this.rendererError = error;
      this.pendingMessages = [];
      this.onRendererMessage({ type: "error", error });
    }

    sendMessage(message) {
      if (this.renderer) {
        this.renderer.send(message);
      } else if (!this.rendererError) {
        this.pendingMessages.push(message);
      }
    }

    normalizePlaybackRate(value) {
      return Number.isFinite(value)
        ? Math.min(MAX_AUDIO_PLAYBACK_RATE, Math.max(MIN_AUDIO_PLAYBACK_RATE, value))
        : 1;
    }

    startPlayback(delaySeconds, playbackRate) {
      const rate = this.normalizePlaybackRate(playbackRate);
      const delay = Number.isFinite(delaySeconds)
        ? Math.min(MAX_AUDIO_DELAY_SECONDS, Math.max(DEFAULT_AUDIO_DELAY_SECONDS, delaySeconds))
        : DEFAULT_AUDIO_DELAY_SECONDS;
      this.sendMessage({
        type: "start",
        delayFrames: Math.round(delay * this.context.sampleRate),
        playbackRate: rate,
      });
      this.playbackStarted = true;
      this.playbackRate = rate;
    }

    resetPlayback() {
      this.playbackHeld = false;
      this.submittedFrames = 0;
      this.outputPosition = null;
      this.outputHistory = [];
      this.pendingMessages = [];
      this.playbackStarted = false;
      this.resampler?.reset();
      if (this.renderer) this.sendMessage({ type: "reset" });
      else if (!this.rendererError) this.pendingMessages.push({ type: "reset" });
    }

    feed(
      payload,
      codec = "pcma",
      delaySeconds = DEFAULT_AUDIO_DELAY_SECONDS,
      playbackRate = 1,
      timestamp = null,
    ) {
      const codecName = typeof codec === "string" ? codec.toLowerCase() : codec;
      const decode = Object.prototype.hasOwnProperty.call(G711_DECODERS, codecName)
        ? G711_DECODERS[codecName]
        : null;
      if (!decode || !payload?.length) return;
      if (!this.context) this.context = this.contextFactory();
      this.ensureRenderer();

      const sampleRate = this.context.sampleRate;
      if (!this.resampler || this.resampler.sampleRate !== sampleRate) {
        this.resampler = new StreamingLinearResampler(sampleRate);
      }
      const rate = this.normalizePlaybackRate(playbackRate);
      const normalizedTimestamp = normalizeAudioTimestamp(timestamp);
      const codecChanged = this.audioCodec !== null && this.audioCodec !== codecName;
      let timestampGap = 0;
      let timestampDiscontinuity = false;
      if (normalizedTimestamp === null) {
        this.nextAudioTimestamp = null;
        this.audioCodec = codecName;
      } else if (this.nextAudioTimestamp === null || this.audioCodec !== codecName) {
        this.nextAudioTimestamp = (normalizedTimestamp + payload.length) % RTP_TIMESTAMP_MODULUS;
        this.audioCodec = codecName;
      } else {
        const delta = (normalizedTimestamp - this.nextAudioTimestamp + RTP_TIMESTAMP_MODULUS) % RTP_TIMESTAMP_MODULUS;
        if (delta >= RTP_TIMESTAMP_HALF_RANGE) return;
        timestampGap = delta;
        timestampDiscontinuity = delta > MAX_AUDIO_TIMESTAMP_GAP_SAMPLES;
        this.nextAudioTimestamp = (normalizedTimestamp + payload.length) % RTP_TIMESTAMP_MODULUS;
      }
      if (timestampDiscontinuity || codecChanged) this.resetPlayback();
      if (!this.playbackStarted) this.startPlayback(delaySeconds, rate);
      else if (this.playbackRate !== rate) {
        this.sendMessage({ type: "rate", playbackRate: rate });
        this.playbackRate = rate;
      }
      if (timestampGap && !timestampDiscontinuity) {
        const silence = this.resampler.processSilence(timestampGap);
        if (silence.length) {
          this.submittedFrames += silence.length;
          this.sendMessage({ type: "append", samples: silence });
        }
      }
      const samples = this.resampler.processG711(payload, decode);
      if (samples.length) {
        this.submittedFrames += samples.length;
        this.sendMessage({ type: "append", samples });
      }
    }

    holdPlayback(held) {
      this.playbackHeld = held === true;
      this.sendMessage({ type: "hold", held });
    }

    playbackPosition() {
      const rate = this.context?.sampleRate || 48000;
      const latest = this.outputPosition;
      const stamp = this.context?.getOutputTimestamp?.();
      const outputTime = stamp?.contextTime > 0 ? stamp.contextTime
        : (this.context?.currentTime || 0) - (this.context?.baseLatency || 0) - (this.context?.outputLatency || 0);
      let audible = null;
      for (const entry of this.outputHistory) {
        if (entry.contextTime > outputTime) break;
        audible = entry;
      }
      return {
        queuedSeconds: Math.max(0, this.submittedFrames - (latest?.renderedFrames || 0)) / rate,
        playedSeconds: (audible?.renderedFrames || 0) / rate,
        playing: Boolean(latest?.playing),
        held: this.playbackHeld,
      };
    }

    reset() {
      this.resetPlayback();
      this.nextAudioTimestamp = null;
      this.audioCodec = null;
    }

    close() {
      this.resetPlayback();
      const renderer = this.renderer;
      this.renderer = null;
      this.rendererGeneration += 1;
      this.rendererPromise = null;
      this.rendererError = null;
      this.pendingMessages = [];
      renderer?.close?.();
      if (this.context) this.context.close?.();
      this.context = null;
      this.nextAudioTimestamp = null;
      this.audioCodec = null;
      this.resampler = null;
    }
  }

  return { alawByteToPcm16, createAudioWorkletRenderer, G711AudioSink, mulawByteToPcm16, resampleG711 };
}));
